import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Col, Container, Row } from 'react-bootstrap';
import type { IconType } from 'react-icons';
import { LuArrowRight } from 'react-icons/lu';

import RevealSection from './RevealSection';
import SbrIcon from './SbrIcon';

export type ImpactStatItem = {
  value: string;
  label: string;
  Icon: IconType;
  valueClassName?: string;
};

export type ImpactResultsCta = {
  label: string;
  to: string;
};

type ImpactResultsSectionProps = {
  stats: ImpactStatItem[];
  eyebrow?: string;
  title?: ReactNode;
  lead?: string;
  cta?: ImpactResultsCta;
  headerAlign?: 'left' | 'center';
  introColLg?: number;
  className?: string;
  reveal?: boolean;
};

export function ImpactStatsRow({ stats }: { stats: ImpactStatItem[] }) {
  return (
    <div className="sbr-impact-stats">
      {stats.map((stat, index) => (
        <Fragment key={`${stat.value}-${stat.label}`}>
          {index > 0 && <div className="sbr-impact-stats__divider" aria-hidden />}
          <article className="sbr-impact-stat">
            <div className="sbr-impact-stat__icon" aria-hidden>
              <SbrIcon icon={stat.Icon} size={28} />
            </div>
            <div className="sbr-impact-stat__copy">
              <div className={`sbr-impact-stat__value fw-bold${stat.valueClassName ? ` ${stat.valueClassName}` : ''}`}>
                {stat.value}
              </div>
              <p className="sbr-impact-stat__label mb-0">{stat.label}</p>
            </div>
          </article>
        </Fragment>
      ))}
    </div>
  );
}

export default function ImpactResultsSection({
  stats,
  eyebrow,
  title,
  lead,
  cta,
  headerAlign = 'left',
  introColLg = 3,
  className = '',
  reveal = false,
}: ImpactResultsSectionProps) {
  const hasHeader = Boolean(eyebrow || title || lead || cta);
  const centered = headerAlign === 'center';

  const content = (
    <Container>
      {centered ? (
        <>
          {hasHeader && (
            <Row className="justify-content-center text-center mb-4 mb-lg-5">
              <Col lg={8} xl={7}>
                {eyebrow && (
                  <p className="sbr-section__eyebrow home-section-eyebrow text-sbr-blue mb-2">{eyebrow}</p>
                )}
                {title && <h2 className="sbr-section__title fw-bold mb-0">{title}</h2>}
                {lead && (
                  <p className={`sbr-impact-intro__lead text-white-50 mx-auto${cta ? ' mb-4' : ' mb-0'}`}>{lead}</p>
                )}
                {cta && (
                  <Button variant="secondary" size="lg" className="fw-semibold" as={Link as any} to={cta.to}>
                    {cta.label}
                    <SbrIcon icon={LuArrowRight} className="ms-2" size={20} />
                  </Button>
                )}
              </Col>
            </Row>
          )}
          <ImpactStatsRow stats={stats} />
        </>
      ) : (
        <Row className="align-items-center gy-4 gy-lg-0">
          {hasHeader && (
            <Col md={introColLg} lg={introColLg}>
              {eyebrow && (
                <p className="sbr-section__eyebrow home-section-eyebrow text-sbr-blue mb-2">{eyebrow}</p>
              )}
              {title && <h2 className="sbr-section__title sbr-section__title--accent fw-bold mb-3">{title}</h2>}
              {lead && (
                <p className={`sbr-impact-intro__lead text-white-50${cta ? ' mb-4' : ' mb-0'}`}>{lead}</p>
              )}
              {cta && (
                <Button variant="secondary" size="lg" className="fw-semibold" as={Link as any} to={cta.to}>
                  {cta.label}
                  <SbrIcon icon={LuArrowRight} className="ms-2" size={20} />
                </Button>
              )}
            </Col>
          )}
          <Col
            xs={12}
            md={hasHeader ? 12 - introColLg : 12}
            lg={hasHeader ? 12 - introColLg : 12}
            className="sbr-impact-section__stats-col ms-lg-auto"
          >
            <ImpactStatsRow stats={stats} />
          </Col>
        </Row>
      )}
    </Container>
  );

  return (
    <section className={`sbr-impact-section hero-pattern text-white py-5 py-lg-6 ${className}`.trim()}>
      {reveal ? <RevealSection>{content}</RevealSection> : content}
    </section>
  );
}
